import { Head, router, usePage } from "@inertiajs/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	PayoutsTab,
	type PayoutsTabProps,
} from "@/components/admin-portal/bonus-ro/payouts-tab";
import {
	RulesTab,
	type RulesTabProps,
} from "@/components/admin-portal/bonus-ro/rules-tab";
import {
	TherapistsTab,
	type TherapistsTabProps,
} from "@/components/admin-portal/bonus-ro/therapists-tab";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { GlobalPageProps } from "@/types/globals";

// Only the active tab's list is loaded; the others stay undefined.
interface PageProps
	extends Omit<RulesTabProps, "routes">,
		Omit<TherapistsTabProps, "routes">,
		Omit<PayoutsTabProps, "routes"> {
	/** one of TABS; the controller falls back to the first */
	tab: string;
}

type BonusRoRoutes = NonNullable<
	GlobalPageProps["adminPortal"]["router"]["adminPortal"]["bonusRo"]
>;

const TABS: {
	key: string;
	labelKey: string;
	render: (props: PageProps, routes: BonusRoRoutes) => ReactNode;
}[] = [
	{
		key: "rules",
		labelKey: "tabs.rules",
		render: (props, routes) => <RulesTab {...props} routes={routes} />,
	},
	{
		key: "therapists",
		labelKey: "tabs.therapists",
		render: (props, routes) => <TherapistsTab {...props} routes={routes} />,
	},
	{
		key: "payouts",
		labelKey: "tabs.payouts",
		render: (props, routes) => <PayoutsTab {...props} routes={routes} />,
	},
];

export default function Index(props: PageProps) {
	const { t } = useTranslation("bonus-ro");
	// The page is only reachable when bonus_ro_menu is present.
	const routes = usePage<GlobalPageProps>().props.adminPortal.router.adminPortal
		.bonusRo as BonusRoRoutes;
	const active = TABS.find((tab) => tab.key === props.tab) ?? TABS[0];

	return (
		<>
			<Head title={t("title")} />

			<div className="flex flex-col gap-4 p-4">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
					<p className="text-sm text-muted-foreground">{t("description")}</p>
				</div>

				<Tabs
					value={active.key}
					onValueChange={(tab) =>
						router.get(
							window.location.pathname,
							{ tab },
							{ preserveScroll: true },
						)
					}
				>
					<TabsList>
						{TABS.map((tab) => (
							<TabsTrigger key={tab.key} value={tab.key}>
								{t(tab.labelKey)}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>

				{active.render(props, routes)}
			</div>
		</>
	);
}
